import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  ArrowLeft,
  CheckSquare,
  ChevronRight,
  FileText,
  Info,
  LayoutGrid,
  Library,
  Link2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { SpaceDocTree } from './SpaceDocTree'
import { AddProjectDialog, SpaceDetailsDrawer, SpaceStarMenuItem } from './SpaceDialogs'
import { LocationSwatch } from './SpaceSwatch'
import { ROW_ACTIVE, ROW_IDLE, RowCount, SectionHeader, rowClass } from './SidebarRows'
import { canDropDocOnProject } from './sidebarDnd'
import { SpaceProjectRow } from './SpaceProjectRow'
import { spaceOpenCount, useOpenTaskCounts } from './useSpaceCounts'
import { useDeleteProject } from './useDeleteProject'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import {
  useChildProjects,
  useCycles,
  usePages,
  useProjectMutations,
  useProjects,
  useSavedFilters,
} from '@/lib/db/rxdb/hooks'
import { ViewIcon } from '@/components/features/saved-filters/viewIcons'
import { AppLayoutContext } from '@/context/AppLayoutContext'
import useLoginStore from '@/store/LoginStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { useProjectReorder } from '@/routes/features/projects/hooks/useProjectReorder'
import { usePageReorder } from '@/routes/features/pages/hooks/usePageReorder'
import { useCreatePage } from '@/routes/features/pages/hooks/useCreatePage'
import { useMoveDoc } from '@/routes/features/pages/hooks/useMoveDoc'
import { sortByRank } from '@/routes/features/spaces/utils/spaceDocs'
import { cn } from '@/lib/utils'

const MAX_SIDEBAR_DOCS = 12
const SPACE_TASK_VIEWS = new Set(['list', 'board', 'calendar', 'gantt'])

interface SpaceDrillInProps {
  space: ProjectDocument
  workspaceSlug: string
  onBack: () => void
}

/**
 * The sidebar drilled into one space (design 21 C / 22a): the whole sidebar
 * becomes that space — its tasks and cycles, projects, the docs shared by all
 * its projects, and its saved views. A project's own Tasks / Docs / Views /
 * Cycles are tabs in the project's top bar, not rows here.
 */
export function SpaceDrillIn({ space, workspaceSlug, onBack }: SpaceDrillInProps) {
  const { t } = useTranslation('projects')
  const { t: tNav } = useTranslation('navigation')
  const navigate = useNavigate()
  const layoutContext = useContext(AppLayoutContext)
  const userId = useLoginStore((s) => s.userId)
  const pathname = useRouterState({ select: (s) => s.location.pathname.replace(/\/+$/, '') })
  const searchTab = useRouterState({
    select: (s) => {
      const raw = (s.location.search as { tab?: unknown } | undefined)?.tab
      return typeof raw === 'string' ? raw : undefined
    },
  })

  const spaceKey = space.projectKey.toLowerCase()
  const spaceBase = `/${workspaceSlug}/spaces/${spaceKey}`

  const { projects: allProjects } = useProjects()
  const { projects: childProjects } = useChildProjects(space.id)
  const projects = useMemo(() => sortByRank((childProjects ?? []).filter((p) => !p.isDeleted)), [childProjects])
  const projectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects])
  const counts = useOpenTaskCounts()
  const { pages } = usePages()
  const { cycles } = useCycles()
  const { savedFilters } = useSavedFilters()

  const spaceDocs = useMemo(() => pages.filter((p) => !p.isDeleted && p.projectId === space.id), [pages, space.id])
  const rootDocs = useMemo(
    () =>
      spaceDocs
        .filter((p) => !p.parentPageId)
        .sort((a, b) => (a.sortOrder && b.sortOrder ? a.sortOrder.localeCompare(b.sortOrder) : a.createdOn - b.createdOn)),
    [spaceDocs],
  )
  const sidebarRoots = useMemo(() => rootDocs.slice(0, MAX_SIDEBAR_DOCS), [rootDocs])
  const docCountByProject = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of pages) if (!p.isDeleted && projectIds.has(p.projectId)) map.set(p.projectId, (map.get(p.projectId) ?? 0) + 1)
    return map
  }, [pages, projectIds])
  const totalDocs = spaceDocs.length + [...docCountByProject.values()].reduce((a, b) => a + b, 0)

  const cycleProjects = projects.filter((p) => p.isCyclesEnabled)
  const activeCycleByProject = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>()
    for (const c of cycles) if (!c.isDeleted && c.status === 'active' && projectIds.has(c.projectId)) map.set(c.projectId, c)
    return map
  }, [cycles, projectIds])

  const views = useMemo(
    () =>
      savedFilters
        .filter(
          (f) =>
            !f.isDeleted &&
            ((f.scope === 'space' && f.scopeEntityId === space.id) ||
              (f.scope === 'project' && !!f.scopeEntityId && projectIds.has(f.scopeEntityId))) &&
            (f.permission !== 'private' || f.ownerId === userId),
        )
        .sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.name.localeCompare(b.name)),
    [savedFilters, space.id, projectIds, userId],
  )

  const { handleReorder, draggingId, setDraggingId, dropIndicator, setDropIndicator } = useProjectReorder(projects)
  const docReorder = usePageReorder(spaceDocs)
  const createSpaceDoc = useCreatePage({
    projectId: space.id,
    projectKey: spaceKey,
    workspaceSlug,
    navigateToPage: (slug) =>
      navigate({ to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug', params: { workspaceSlug, spaceKey, pageSlug: slug } }),
  })

  const moveDoc = useMoveDoc(pages)
  const projectOrder = useMemo(() => projects.map((p) => p.id), [projects])
  const canAcceptDoc = useCallback(
    (projectId: string, docId: string) => canDropDocOnProject(pages.find((p) => p.id === docId), projectId),
    [pages],
  )
  const [newTaskProjectId, setNewTaskProjectId] = useState<string | null>(null)

  const [showAddProject, setShowAddProject] = useState(false)
  const [cyclesOpen, setCyclesOpen] = useState(false)

  const suffix = pathname.startsWith(`${spaceBase}/`) ? (pathname.slice(spaceBase.length + 1).split('/')[0] ?? null) : null
  const isAllDocsActive = pathname === spaceBase && searchTab === 'docs'
  const isAllTasksActive =
    (pathname === spaceBase && (searchTab === undefined || searchTab === 'tasks')) ||
    (suffix !== null && SPACE_TASK_VIEWS.has(suffix))
  const { activeProjectKey, projectPagesBase } = (() => {
    const segments = pathname.split('/').filter(Boolean)
    if (segments[1] === 'spaces' && segments[3] === 'projects') {
      const key = segments[4] ?? null
      return { activeProjectKey: key, projectPagesBase: segments[5] === 'pages' && segments[6] ? `/${segments.slice(0, 5).join('/')}/pages` : null }
    }
    if (segments[1] === 'projects') {
      const key = segments[2] ?? null
      return { activeProjectKey: key, projectPagesBase: segments[3] === 'pages' && segments[4] ? `/${segments.slice(0, 3).join('/')}/pages` : null }
    }
    return { activeProjectKey: null, projectPagesBase: null }
  })()

  // Reading one of a project's docs opens that project's docs under its row, with
  // the doc you're on marked (design 32b). Leaving the doc folds them away again.
  const docProjectId = projectPagesBase
    ? (projects.find((p) => p.projectKey.toLowerCase() === activeProjectKey)?.id ?? null)
    : null
  const [foldedProjectId, setFoldedProjectId] = useState<string | null>(null)
  useEffect(() => {
    if (!docProjectId) setFoldedProjectId(null)
  }, [docProjectId])

  const goToView = (view: SavedFilterDocument) => {
    if (view.scope === 'space') {
      void navigate({ to: '/$workspaceSlug/spaces/$spaceKey/views/$viewId', params: { workspaceSlug, spaceKey, viewId: view.id } })
      return
    }
    const project = projects.find((p) => p.id === view.scopeEntityId)
    if (!project) return
    void navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views/$viewId',
      params: { workspaceSlug, spaceKey, projectKey: project.projectKey.toLowerCase(), viewId: view.id },
    })
  }

  const spaceOpen = spaceOpenCount(space, allProjects, counts)
  const summary = [
    tNav('spaceNav.summary.projects', { count: projects.length }),
    tNav('spaceNav.summary.docs', { count: totalDocs }),
    activeCycleByProject.size > 0
      ? tNav('spaceNav.summary.cyclesRunning', { count: activeCycleByProject.size })
      : tNav('spaceNav.summary.open', { count: spaceOpen }),
  ].join(' · ')

  return (
    <div className="flex flex-col pb-3" data-testid="space-drill-in">
      <SpaceHeader
        space={space}
        workspaceSlug={workspaceSlug}
        summary={summary}
        onBack={onBack}
        onAddProject={() => setShowAddProject(true)}
        onAddDoc={() => void createSpaceDoc()}
      />

      <div className="px-3 pb-1.5">
        <button
          type="button"
          onClick={() => layoutContext?.openSearch()}
          className="flex h-[30px] w-full min-w-0 items-center gap-2 rounded-md bg-secondary px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Search className="size-3.5 shrink-0" />
          <span className="flex-1 truncate text-left">{tNav('spaceNav.searchIn', { name: space.name })}</span>
        </button>
      </div>

      <ul className="flex flex-col gap-px px-2 py-1.5">
        <li>
          <button
            type="button"
            className={rowClass(isAllTasksActive)}
            aria-current={isAllTasksActive ? 'page' : undefined}
            onClick={() => navigate({ to: '/$workspaceSlug/spaces/$spaceKey', params: { workspaceSlug, spaceKey } })}
          >
            <CheckSquare className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{tNav('spaceNav.allTasks')}</span>
            {spaceOpen > 0 && <RowCount>{spaceOpen}</RowCount>}
          </button>
        </li>
        <li>
          <button
            type="button"
            className={rowClass(false)}
            aria-expanded={cyclesOpen}
            onClick={() => setCyclesOpen((v) => !v)}
          >
            <RefreshCw className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{tNav('spaceNav.cycles')}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {tNav('spaceNav.cyclesActive', { count: activeCycleByProject.size })}
            </span>
            <ChevronRight
              className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none', cyclesOpen && 'rotate-90')}
            />
          </button>
          {cyclesOpen && (
            <ul className="mt-px flex flex-col gap-px">
              {cycleProjects.length === 0 ? (
                <li className="py-1 pl-9 pr-3 text-[12.5px] text-muted-foreground">{tNav('spaceNav.noCycles')}</li>
              ) : (
                cycleProjects.map((project) => {
                  const active = activeCycleByProject.get(project.id)
                  const pk = project.projectKey.toLowerCase()
                  return (
                    <li key={project.id}>
                      <button
                        type="button"
                        className={rowClass(false, 'pl-8')}
                        onClick={() =>
                          active
                            ? navigate({
                                to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/cycles/$cycleId',
                                params: { workspaceSlug, spaceKey, projectKey: pk, cycleId: active.id },
                              })
                            : navigate({
                                to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
                                params: { workspaceSlug, spaceKey, projectKey: pk },
                                search: { projectTab: 'cycles' },
                              })
                        }
                      >
                        <LocationSwatch location={project} size={14} />
                        <span className="min-w-0 truncate">{project.name}</span>
                        <span className="ml-auto min-w-0 truncate text-xs text-muted-foreground">
                          {active?.name ?? tNav('spaceNav.noActiveCycle')}
                        </span>
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          )}
        </li>
      </ul>

      <section aria-label={t('title')} className="py-1.5">
        <SectionHeader label={t('title')} onAdd={() => setShowAddProject(true)} addLabel={t('sidebar.space.addNewProject')} />
        {projects.length === 0 ? (
          <p className="px-[18px] py-1 text-[12.5px] text-muted-foreground">{t('sidebar.space.noProjects')}</p>
        ) : (
          <ul className="flex flex-col gap-px px-2">
            {projects.map((project) => (
              <li key={project.id}>
                <SpaceProjectRow
                  project={project}
                  spaceKey={spaceKey}
                  workspaceSlug={workspaceSlug}
                  isActive={activeProjectKey === project.projectKey.toLowerCase()}
                  openCount={counts.get(project.id) ?? 0}
                  orderedIds={projectOrder}
                  draggingId={draggingId}
                  onDragStart={setDraggingId}
                  onDragEnd={() => setDraggingId(null)}
                  onReorder={handleReorder}
                  dropIndicator={dropIndicator}
                  onDropIndicatorChange={setDropIndicator}
                  canAcceptDoc={(docId) => canAcceptDoc(project.id, docId)}
                  onDropDoc={(docId) =>
                    void moveDoc(docId, { projectId: project.id, parentPageId: null }, { destinationName: project.name })
                  }
                  onNewTask={() => setNewTaskProjectId(project.id)}
                  {...(docProjectId === project.id && {
                    docsExpanded: foldedProjectId !== project.id,
                    onToggleDocs: () => setFoldedProjectId((id) => (id === project.id ? null : project.id)),
                  })}
                />
                {docProjectId === project.id && foldedProjectId !== project.id && projectPagesBase && (
                  <ProjectDocBranch
                    project={project}
                    pages={pages}
                    workspaceSlug={workspaceSlug}
                    spaceKey={spaceKey}
                    currentPath={pathname}
                    pagesBasePath={projectPagesBase}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label={tNav('spaceNav.spaceDocs')} className="py-1.5">
        <SectionHeader
          label={tNav('spaceNav.spaceDocs')}
          onAdd={() => void createSpaceDoc()}
          addLabel={tNav('spaceNav.newSpaceDoc')}
        />
        {rootDocs.length === 0 && (
          <p className="px-[18px] py-1 text-[12.5px] text-muted-foreground">{t('sidebar.space.noDocs')}</p>
        )}
        <SpaceDocTree
          spaceDocs={spaceDocs}
          allPages={pages}
          roots={sidebarRoots}
          workspaceSlug={workspaceSlug}
          spaceKey={spaceKey}
          currentPath={pathname}
          pagesBasePath={`${spaceBase}/pages`}
          onCreateSubPage={(parentId) => void createSpaceDoc(parentId)}
          onReorder={docReorder.handleReorder}
          onMoveIn={(docId, parentPageId) =>
            void moveDoc(docId, { projectId: space.id, parentPageId }, { destinationName: space.name })
          }
          footer={
            <button
              type="button"
              className={cn(
                'relative flex h-8 w-full min-w-0 items-center gap-1.5 rounded-md pl-1 pr-2.5 text-left text-sm transition-colors',
                isAllDocsActive ? ROW_ACTIVE : ROW_IDLE,
              )}
              aria-current={isAllDocsActive ? 'page' : undefined}
              onClick={() => navigate({ to: '/$workspaceSlug/spaces/$spaceKey', params: { workspaceSlug, spaceKey }, search: { tab: 'docs' } })}
            >
              {/* Same 14px chevron slot as the doc rows, so the icon lines up. */}
              <span aria-hidden className="w-3.5 shrink-0" />
              <Library className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{tNav('spaceNav.allDocs')}</span>
              {totalDocs > 0 && <RowCount>{totalDocs}</RowCount>}
            </button>
          }
        />
      </section>

      <section aria-label={tNav('spaceNav.views')} className="py-1.5">
        <SectionHeader
          label={tNav('spaceNav.views')}
          onAdd={() => navigate({ to: '/$workspaceSlug/spaces/$spaceKey', params: { workspaceSlug, spaceKey } })}
          addLabel={tNav('spaceNav.newView')}
        />
        {views.length === 0 ? (
          <p className="px-[18px] py-1 text-[12.5px] text-muted-foreground">{tNav('spaceNav.noViews')}</p>
        ) : (
          <ul className="flex flex-col gap-px px-2">
            {views.map((view) => {
              const project = view.scope === 'project' ? projects.find((p) => p.id === view.scopeEntityId) : null
              const viewPath = project
                ? `${spaceBase}/projects/${project.projectKey.toLowerCase()}/views/${view.id}`
                : `${spaceBase}/views/${view.id}`
              const active = pathname === viewPath
              // The name takes the full width; where the view lives is in the tooltip (design 22c).
              return (
                <li key={view.id}>
                  <Tooltip delayDuration={400}>
                    <TooltipTrigger asChild>
                      <button type="button" className={rowClass(active)} onClick={() => goToView(view)}>
                        <ViewIcon icon={view.icon} className="text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{view.name}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{project?.name ?? space.name}</TooltipContent>
                  </Tooltip>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {newTaskProjectId && (
        <QuickCreateTaskDialog
          open
          onOpenChange={(open) => {
            if (!open) setNewTaskProjectId(null)
          }}
          defaultProjectId={newTaskProjectId}
        />
      )}

      <AddProjectDialog
        spaceId={space.id}
        workspaceSlug={workspaceSlug}
        spaceKey={spaceKey}
        lastSiblingSortOrder={projects.at(-1)?.sortOrder ?? null}
        open={showAddProject}
        onOpenChange={setShowAddProject}
      />
    </div>
  )
}

/**
 * The docs of the project you're reading a doc in, nested under its row
 * (design 32b). Same rows and drag rules as the space's own docs; parents
 * stay closed with a count and open in place, up to three levels (24a).
 */
function ProjectDocBranch({
  project,
  pages,
  workspaceSlug,
  spaceKey,
  currentPath,
  pagesBasePath,
}: {
  project: ProjectDocument
  pages: ReadonlyArray<PageDocument>
  workspaceSlug: string
  spaceKey: string
  currentPath: string
  pagesBasePath: string
}) {
  const navigate = useNavigate()
  const projectKey = project.projectKey.toLowerCase()
  const docs = useMemo(() => pages.filter((p) => !p.isDeleted && p.projectId === project.id), [pages, project.id])
  const roots = useMemo(() => sortByRank(docs.filter((p) => !p.parentPageId)), [docs])
  const reorder = usePageReorder(docs)
  const moveDoc = useMoveDoc(pages)
  const createDoc = useCreatePage({
    projectId: project.id,
    projectKey,
    workspaceSlug,
    navigateToPage: (slug) =>
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
        params: { workspaceSlug, spaceKey, projectKey, pageSlug: slug },
      }),
  })

  return (
    <div className="mt-px" data-testid="sidebar-project-docs">
      <SpaceDocTree
        spaceDocs={docs}
        allPages={pages}
        roots={roots}
        workspaceSlug={workspaceSlug}
        spaceKey={spaceKey}
        projectKey={projectKey}
        currentPath={currentPath}
        pagesBasePath={pagesBasePath}
        baseIndentPx={22}
        onCreateSubPage={(parentId) => void createDoc(parentId)}
        onReorder={reorder.handleReorder}
        onMoveIn={(docId, parentPageId) =>
          void moveDoc(docId, { projectId: project.id, parentPageId }, { destinationName: project.name })
        }
      />
    </div>
  )
}

/** "← All spaces", the space's icon, name and summary, and its ⋯ menu. */
function SpaceHeader({
  space,
  workspaceSlug,
  summary,
  onBack,
  onAddProject,
  onAddDoc,
}: {
  space: ProjectDocument
  workspaceSlug: string
  summary: string
  onBack: () => void
  onAddProject: () => void
  onAddDoc: () => void
}) {
  const { t } = useTranslation('projects')
  const { t: tNav } = useTranslation('navigation')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const { updateProject } = useProjectMutations()
  const spaceKey = space.projectKey.toLowerCase()
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(space.name)
  const [showDetails, setShowDetails] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const {
    isLastSpace,
    openTaskCount,
    isLoading: deletePlanLoading,
    deleteProjectTree,
  } = useDeleteProject(space.id, showDeleteConfirm)
  const renameRef = useRef<HTMLInputElement>(null)
  // The menu returns focus to its trigger on close, which would blur (and so
  // commit) the rename input the instant it mounts. Skip that when renaming.
  const renameRequestedRef = useRef(false)

  const commitRename = async () => {
    const trimmed = renameValue.trim()
    setIsRenaming(false)
    if (!trimmed || trimmed === space.name) return
    try {
      await updateProject(space.id, { name: trimmed })
    } catch {
      toast.error(t('sidebar.toast.spaceRenameFailed'))
    }
  }

  return (
    <div className="px-3 pb-2 pt-2.5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          data-testid="sidebar-all-spaces"
          className="flex h-7 items-center gap-1.5 rounded-md px-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          {tNav('spaceNav.allSpaces')}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('sidebar.space.optionsAria')}
              className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-52"
            onCloseAutoFocus={(e) => {
              if (!renameRequestedRef.current) return
              renameRequestedRef.current = false
              e.preventDefault()
              renameRef.current?.focus()
              renameRef.current?.select()
            }}
          >
            <SpaceStarMenuItem spaceId={space.id} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onAddProject}>
              <LayoutGrid className="mr-2 size-4" />
              {t('sidebar.space.addNewProject')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onAddDoc}>
              <FileText className="mr-2 size-4" />
              {t('sidebar.space.addNewPage')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setShowDetails(true)}>
              <Info className="mr-2 size-4" />
              {t('sidebar.space.details')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                void navigator.clipboard.writeText(`${window.location.origin}/${workspaceSlug}/spaces/${spaceKey}`)
                toast.success(t('sidebar.toast.linkCopied'))
              }}
            >
              <Link2 className="mr-2 size-4" />
              {t('sidebar.space.copyLink')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                setRenameValue(space.name)
                setIsRenaming(true)
                renameRequestedRef.current = true
              }}
            >
              <Pencil className="mr-2 size-4" />
              {t('sidebar.space.rename')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="items-start text-destructive focus:text-destructive"
              disabled={isLastSpace}
              onSelect={() => setShowDeleteConfirm(true)}
              data-testid="sidebar-space-delete"
            >
              <Trash2 className="mr-2 mt-0.5 size-4" />
              <span className="flex flex-col">
                {t('sidebar.space.delete')}
                {isLastSpace && (
                  <span className="text-[11.5px] text-muted-foreground">{t('sidebar.space.deleteLastSpace')}</span>
                )}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-1.5 flex min-w-0 items-center gap-2.5 px-1.5">
        <UnifiedIconPicker
          iconType={space.iconType}
          icon={space.icon}
          iconColor={space.iconColor}
          onValueChange={async (value) => {
            try {
              await updateProject(space.id, {
                icon: value?.icon ?? null,
                iconType: value?.iconType ?? null,
                iconColor: value?.iconColor ?? null,
              })
            } catch {
              toast.error(t('sidebar.toast.iconUpdateFailed'))
            }
          }}
        >
          <button
            type="button"
            aria-label={t('sidebar.space.changeIconAria')}
            className="shrink-0 rounded-lg transition-opacity hover:opacity-85"
          >
            <ProjectIcon
              iconType={space.iconType}
              icon={space.icon}
              iconColor={space.iconColor}
              size={28}
              variant="badge"
              fallbackLabel={space.name}
            />
          </button>
        </UnifiedIconPicker>
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={renameRef}
              value={renameValue}
              aria-label={t('sidebar.space.renameAria')}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => void commitRename()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void commitRename()
                }
                if (e.key === 'Escape') setIsRenaming(false)
              }}
              className="w-full border-b border-brand bg-transparent text-base font-semibold outline-none md:text-[15px]"
            />
          ) : (
            <h2 className="truncate text-[15px] font-semibold leading-tight text-foreground" data-testid="sidebar-space-name">
              {space.name}
            </h2>
          )}
          <p className="truncate text-[11.5px] text-muted-foreground">{summary}</p>
        </div>
      </div>

      <SpaceDetailsDrawer space={space} open={showDetails} onOpenChange={setShowDetails} />
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.space.deleteTitle', { name: space.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {openTaskCount > 0
                ? t('sidebar.space.deleteBlockedDescription', { count: openTaskCount, name: space.name })
                : t('sidebar.space.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletePlanLoading || openTaskCount > 0 || isLastSpace}
              onClick={() => {
                void (async () => {
                  try {
                    await deleteProjectTree()
                    toast.success(t('sidebar.toast.spaceDeleted'))
                    void navigate({ to: '/$workspaceSlug', params: { workspaceSlug } })
                  } catch {
                    toast.error(t('sidebar.toast.spaceDeleteFailed'))
                  }
                })()
              }}
            >
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
